import { PATH_FILTER_IDS } from "../types/PathFilterId";
import { toStringArray } from "./buildQueryString";
import { buildSubmitUrl } from "./buildSubmitUrl";
import { SORT_PARAM } from "../constants";
/**
 * Resolves the human-readable label for a single filter value.
 * Looks in pathFilters options first, then facet data.
 * Falls back to searching by item.value field (e.g. body_type group aliases).
 */
function resolveLabel(key, value, filterConfig, facetData, pathFilters) {
    var _a;
    const staticFilter = pathFilters.find((f) => f.id === key);
    if (staticFilter) {
        const opt = staticFilter.options.find((o) => o.value === value);
        return (_a = opt === null || opt === void 0 ? void 0 : opt.label) !== null && _a !== void 0 ? _a : value;
    }
    const config = filterConfig.find((f) => f.id === key);
    if ((config === null || config === void 0 ? void 0 : config.facet_group) != null) {
        const group = facetData[config.facet_group];
        if (group) {
            // Direct key lookup (most items)
            const item = group.facet_data[value];
            if (item === null || item === void 0 ? void 0 : item.label)
                return item.label;
            // Fallback: search by item.value field (e.g. 'furgon_group' stored as value
            // inside the 'furgon' key after the body-type group transform)
            const byValue = Object.values(group.facet_data).find((i) => i.value === value);
            if (byValue === null || byValue === void 0 ? void 0 : byValue.label)
                return byValue.label;
        }
    }
    return value;
}
/**
 * Builds the URL that results from removing a single value from a filter.
 * If it's the last value, the key is removed entirely.
 */
function buildRemoveUrl(key, value, committedState, pathname, search) {
    const current = committedState[key];
    let next;
    if (Array.isArray(current)) {
        const remaining = current.filter((v) => v !== value);
        next = Object.assign(Object.assign({}, committedState), { [key]: remaining.length > 0 ? remaining : undefined });
    }
    else {
        next = Object.assign(Object.assign({}, committedState), { [key]: undefined });
    }
    return buildSubmitUrl(next, pathname, search);
}
/**
 * Path segment (offer_type), path filters and sort: excluded from removable chips
 * and active-filter counts, and preserved when clearing all filters.
 */
export const CLEAR_ALL_PRESERVED_KEYS = [
    "offer_type",
    ...PATH_FILTER_IDS,
    SORT_PARAM,
];
const PATH_FILTER_KEYS = new Set(CLEAR_ALL_PRESERVED_KEYS);
/**
 * Returns an ordered list of active filter chips derived from committedState.
 * Each chip carries a human-readable label and the URL to navigate to when removed.
 * Path filters (finance_type, monthly_price) and offer_type are excluded.
 */
export function getActiveFilterChips(committedState, filterConfig, facetData, pathFilters, pathname, search) {
    const chips = [];
    for (const [key, raw] of Object.entries(committedState)) {
        if (PATH_FILTER_KEYS.has(key))
            continue;
        if (raw === undefined || raw === null || raw === "")
            continue;
        const values = toStringArray(raw);
        for (const value of values) {
            if (!value)
                continue;
            chips.push({
                key,
                value,
                valueLabel: resolveLabel(key, value, filterConfig, facetData, pathFilters),
                removeUrl: buildRemoveUrl(key, value, committedState, pathname, search),
            });
        }
    }
    return chips;
}
/**
 * Counts active filters in committedState that are not path filters or sort.
 * Matches {@link getActiveFilterChips}: offer_type, finance_type, monthly_price and sort are excluded
 * so badge counts reflect user-applied filters only.
 */
export function countActiveFilters(committedState) {
    let count = 0;
    for (const [key, raw] of Object.entries(committedState)) {
        if (PATH_FILTER_KEYS.has(key))
            continue;
        if (raw === undefined || raw === null || raw === "")
            continue;
        const values = toStringArray(raw);
        count += values.filter(Boolean).length;
    }
    return count;
}
/**
 * URL to navigate to when clearing all filters.
 * Path filters and sort are preserved (path segments cannot be removed via chips; sort is a separate control).
 */
export function buildClearAllUrl(committedState, pathname, search) {
    const preserved = {};
    for (const key of CLEAR_ALL_PRESERVED_KEYS) {
        if (committedState[key] !== undefined)
            preserved[key] = committedState[key];
    }
    return buildSubmitUrl(preserved, pathname, search);
}
